import React, { useState, useMemo } from 'react';
import './Pages/PagesCss/Home.css';
import { NavLink } from 'react-router-dom';
import logo from '../components/assets/logo.png';

const Home = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const toolCategories = [
    {
      title: "Text Styling",
      icon: (
      <div className="category-logo-icon">
        <img src={logo} alt="Style" className="logo-icon-img" />
      </div>
    ),
      tools: [
        { name: "Font Generator", path: "/FontGenerator" },
        { name: "Emojis Generator", path: "/Emojis" },
        { name: "Symbols Generator", path: "/Symbols" },
        { name: "Font Finder", path: "/FontFinder" },
        { name: "Nickname Generator", path: "/NicknameGenerator" },
        { name: "Instagram Bio Formatter", path: "/InstagramBioFormatter" },
        { name: "Unicode Text Normalizer", path: "/UnicodeTextNormalizer" },
        { name: "Text Formatter", path: "/TextFormatter" },
        { name: "ASCII Art Generator", path: "/ASCIIArtGenerator" },
      ]
    },
    {
      title: "Text Converting",
      icon: (
      <div className="category-logo-icon">
        <img src={logo} alt="Style" className="logo-icon-img" />
      </div>
    ),
      tools: [
        { name: "Sentence Case Converter", path: "/SentenceCaseConverter" },
        { name: "Text Reverse", path: "/TextReverse" },
        { name: "Find & Replace", path: "/FindReplace" },
        { name: "JSON To Text", path: "/JsonToTextConverter" },
        { name: "Text To Binary", path: "/TextToBinary" },
        { name: "Csv To Json", path: "/CsvToJsonConverter" },
        { name: "ROT13 Converter", path: "/ROT13Converter" },
        { name: "Caesar Cipher", path: "/CaesarCipher" },
            { name: "Vigenère Cipher", path: "/VigenereCipher" },
      ]
    },
    {
      title: "Text Analysis",
      icon: (
      <div className="category-logo-icon">
        <img src={logo} alt="Style" className="logo-icon-img" />
      </div>
    ),
      tools: [
        { name: "Word Counter", path: "/WordCounter" },
        { name: "Keyword Density Checker", path: "/KeywordDensityChecker" },
        { name: "Letter Frequency Counter", path: "/LetterFrequencyCounter" },
        { name: "Long & Short Word Finder", path: "/LongShortWordFinder" },
        { name: "Readability Checker", path: "/ReadabilityChecker" },
        { name: "Sentence Complexity", path: "/SentenceComplexityAnalyzer" },
        { name: "Passive Voice Detector", path: "/PassiveVoiceDetector" },
        { name: "Character Counter", path: "/CharacterCounter" },
        { name: "SEO Title Length Checker", path: "/SEOTitleLengthChecker" },
      ]
    },
    {
      title: "Text Cleaning",
      icon: (
      <div className="category-logo-icon">
        <img src={logo} alt="Style" className="logo-icon-img" />
      </div>
    ),
      tools: [
        { name: "Duplicate Line Remover", path: "/DuplicateLineRemover" },
        { name: "Whitespace Remover", path: "/WhitespaceRemover" },
        { name: "Line Break Remover", path: "/TextLineBreakRemover" },
        { name: "Unique Words Extractor", path: "/UniqueWordsExtract" },
        { name: "Code Snippet Clean", path: "/CodeSnippetCleaner" },
        { name: "JSON Schema Generator", path: "/JSONSchemaGenerator" },
        { name: "HTML Tag Remover", path: "/HTMLTagRemover" },
        { name: "JavaScript Formatter", path: "/JavaScriptFormatter" },
      ]
    },
    {
      title: "Text Encoding",
      icon: (
      <div className="category-logo-icon">
        <img src={logo} alt="Style" className="logo-icon-img" />
      </div>
    ),
      tools: [
        { name: "Base64 Encoder/Decoder", path: "/Base64Tool" },
        { name: "URL Encoder/Decoder", path: "/URLEncoderDecoder" },
        { name: "Morse Code Converter", path: "/MorseCodeConverter" },
        { name: "Number Extractor", path: "/NumberExtractor" },
        { name: "JWT Decoder", path: "/JWTDecoder" },
        { name: "UUID Generator", path: "/UUIDGenerator" },
        { name: "Hex Encoder / Decoder", path: "/HexEncoderDecoder" },
        { name: "Binary Encoder / Decoder", path: "/BinaryEncoderDecoder" },
      ]
    },
    {
      title: "Text Sorting",
      icon: (
      <div className="category-logo-icon">
        <img src={logo} alt="Style" className="logo-icon-img" />
      </div>
    ),
      tools: [
        { name: "Alphabetical Sorter", path: "/AlphabeticalSorter" },
        { name: "Numeric Sorter", path: "/NumericSorter" },
        { name: "Date Sorter", path: "/DateSorter" },
        { name: "Domain Sorter", path: "/DomainSorter" },
        { name: "Random List Shuffler", path: "/RandomListShuffler" },
        { name: "IP Address Sorter", path: "/IPAddressSorter" },
        { name: "Reverse Sorter", path: "/ReverseSorter" },
        { name: "Password Generator", path: "/PasswordGenerator" },
      ]
    }
  ]; 

  // Flatten all tools for search
  const allTools = useMemo(() => {
    return toolCategories.flatMap(category => 
      category.tools.map(tool => ({
        ...tool,
        category: category.title,
        categoryIcon: category.icon
      }))
    );
  }, []);

  // Filter tools based on search query
  const filteredTools = useMemo(() => {
    if (!searchQuery.trim()) return [];
    
    const query = searchQuery.toLowerCase();
    return allTools.filter(tool => 
      tool.name.toLowerCase().includes(query) ||
      tool.category.toLowerCase().includes(query)
    );
  }, [searchQuery, allTools]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const handleSuggestionClick = (path) => {
    // You can navigate to the tool or just clear the search
    setSearchQuery('');
    setIsSearchFocused(false);
  };

  return (
    <div className="modern-home-container">
      {/* Hero Section */}
      <div className="modern-hero-section">
        <div className="hero-content">
          <h1 className="modern-hero-title">
            Welcome to Font-Max
          </h1>
          <p className="modern-hero-subtitle">
            Your all-in-one platform for advanced text and image processing. 
            Explore powerful utilities to enhance your content creation workflow.
          </p>
          
          {/* Search Bar */}
          <div className="search-container">
            <div className="search-input-wrapper">
              <i className="fa-solid fa-search search-icon"></i>
              <input
                type="text"
                placeholder="Search tools by name or category..."
                value={searchQuery}
                onChange={handleSearch}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
                className="search-input"
              />
              {searchQuery && (
                <button 
                  onClick={clearSearch}
                  className="search-clear-btn"
                  aria-label="Clear search"
                >
                  <i className="fa-solid fa-times"></i>
                </button>
              )}
            </div>
            
            {/* Search Suggestions Dropdown */}
            {isSearchFocused && searchQuery && filteredTools.length > 0 && (
              <div className="search-suggestions">
                <div className="suggestions-header">
                  <span className="suggestion-count">
                    {filteredTools.length} tool{filteredTools.length !== 1 ? 's' : ''} found
                  </span>
                </div>
                {filteredTools.map((tool, index) => (
                  <NavLink
                    key={index}
                    to={tool.path}
                    className="suggestion-item"
                    onClick={() => handleSuggestionClick(tool.path)}
                  >
                    <div className="suggestion-icon">
                      <div className="search-suggestion-logo">
          <img src={logo} alt={tool.category} className="suggestion-logo-img" />
        </div>
                    </div>
                    <div className="suggestion-content">
                      <div className="suggestion-name">{tool.name}</div>
                      <div className="suggestion-category">{tool.category}</div>
                    </div>
                    <i className="fa-solid fa-arrow-right suggestion-arrow"></i>
                  </NavLink>
                ))}
              </div>
            )}
            
            {/* No Results Message */}
            {isSearchFocused && searchQuery && filteredTools.length === 0 && (
              <div className="search-suggestions">
                <div className="no-results">
                  <i className="fa-solid fa-search"></i>
                  <p>No tools found matching "{searchQuery}"</p>
                  <p className="no-results-tip">Try searching with different keywords</p>
                </div>
              </div>
            )}
          </div>

          {/* Stats Section */}
          <div className="hero-stats">
            <div className="stat-item">
              <span className="stat-number">{allTools.length}+</span>
              <span className="stat-label">Tools</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">100%</span>
              <span className="stat-label">Free</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">Instant</span>
              <span className="stat-label">Processing</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tools Section */}
      <div className="tools-section">
        <div className="section-header-3">
          <i className="fa-solid fa-toolbox"></i>
          <h2>Featured Tool Categories</h2>
        </div>
        <p className="section-subtitle">Explore our comprehensive collection of text processing utilities</p>

        <div className="tools-grid">
          {toolCategories.map((category, index) => (
            <div key={index} className="category-card">
              <div className="category-header">
                {category.icon}
                <h3 className="category-title">{category.title}</h3>
              </div>
              <div className="tools-list">
                {category.tools.map((tool, toolIndex) => (
                  <NavLink 
                    key={toolIndex}
                    to={tool.path}
                    className="tool-item"
                  >
                    <span className="tool-name">{tool.name}</span>
                    <i className="fa-solid fa-arrow-right"></i>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="cta-section">
        <div className="cta-content">
          <h2>Ready to Transform Your Text?</h2>
          <p>Start using our powerful tools today and experience the difference</p>
          <div className="cta-buttons">
            <NavLink to="/FontGenerator" className="cta-btn primary">
              <i className="fa-solid fa-play"></i>
              Get Started
            </NavLink>
            <NavLink to="/WordCounter" className="cta-btn secondary">
              <i className="fa-solid fa-explore"></i>
              Explore Tools
            </NavLink>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="content-section">
        <div className="modern-content-container">
          <h1 className="content-title">Welcome to Font-Max.com – Your Ultimate Text Tool Hub</h1>

          <div className="content-grid">
            <div className="content-card">
              <div className="content-header">
                <i className="fa-solid fa-question-circle"></i>
                <h3>What is Font-Max.com?</h3>
              </div>
              <p>At Font-Max.com, we provide a suite of powerful text tools designed to enhance your online experience. Whether you're looking to generate custom fonts, clean up your text, analyze your content, or encode/decode text, we have everything you need. Our user-friendly platform offers an extensive collection of online tools that help you save time and improve productivity.</p>
            </div>

            <div className="content-card">
              <div className="content-header">
                <i className="fa-solid fa-font"></i>
                <h3>Font Generator – Unlock Creative Font Styles</h3>
              </div>
              <p>Looking to create unique font styles? Font-Max's Font Generator allows you to easily generate stylish, creative fonts for your text. With a variety of customizable font options, this tool is perfect for designers, content creators, and anyone looking to enhance their text with fresh, eye-catching fonts.</p>
            </div>

            <div className="content-card">
              <div className="content-header">
                <i className="fa-solid fa-smile"></i>
                <h3>Emojis Generator – Express Yourself with Fun Emojis</h3>
              </div>
              <p>Need to add some personality to your messages or social media posts? The Emojis Generator on Font-Max.com lets you quickly generate and copy a wide array of emojis for every occasion. Perfect for creating engaging content or spicing up your online communication.</p>
            </div>

            <div className="content-card">
              <div className="content-header">
                <i className="fa-solid fa-shapes"></i>
                <h3>Symbols Generator – Add Meaningful Symbols to Your Text</h3>
              </div>
              <p>With Font-Max's Symbols Generator, you can easily insert special symbols, characters, and signs into your text. From hearts and arrows to currency symbols and more, our symbol generator provides endless possibilities for enhancing your content.</p>
            </div>

            <div className="content-card">
              <div className="content-header">
                <i className="fa-solid fa-text-height"></i>
                <h3>Text Case Tools – Format Your Text the Right Way</h3>
              </div>
              <p>Need to convert your text into different cases? Font-Max.com offers various Text Case Conversion tools, including SentenceCase Converter, Text Reverse, and Find & Replace. Whether you're converting text from uppercase to lowercase, reversing text, or cleaning up your content.</p>
            </div>

            <div className="content-card">
              <div className="content-header">
                <i className="fa-solid fa-search"></i>
                <h3>Keyword Density Checker – Optimize Your Content for SEO</h3>
              </div>
              <p>If you're a content creator, blogger, or SEO expert, you know how important keyword optimization is. Our Keyword Density Checker helps you analyze your text and find the perfect balance of keywords for improved SEO performance.</p>
            </div>
          </div>

          <div className="benefits-section">
  <h2>Why Choose Font-Max.com?</h2>
  <div className="benefits-grid">
    <div className="benefit-item">
      <svg className="benefit-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 11.08V12a10 10 0 11-5.93-9.14" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M22 4L12 14.01l-3-3" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <div>
        <strong>Free Tools</strong>
        <span>All tools available for free, no hidden charges</span>
      </div>
    </div>

    <div className="benefit-item">
      <svg className="benefit-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M2 12l3.5 2L8 9l4 11 4-11 2.5 5.5L22 12" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <div>
        <strong>Easy to Use</strong>
        <span>Designed for simplicity and speed</span>
      </div>
    </div>

    <div className="benefit-item">
      <svg className="benefit-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M12 9v4M12 17h.01" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <div>
        <strong>Variety of Tools</strong>
        <span>Comprehensive suite of text utilities</span>
      </div>
    </div>

    <div className="benefit-item">
      <svg className="benefit-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <div>
        <strong>Secure & Private</strong>
        <span>Your information is safe and never stored</span>
      </div>
    </div>

    <div className="benefit-item">
      <svg className="benefit-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <div>
        <strong>Instant Access</strong>
        <span>Use tools online anytime without installation</span>
      </div>
    </div>

    <div className="benefit-item">
      <svg className="benefit-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <div>
        <strong>Fast & Reliable</strong>
        <span>Lightning-fast processing for efficiency</span>
      </div>
    </div>
  </div>
</div>
        </div>
      </div>
    </div>
  );
};

export default Home;